/**
 * Realm authentication classes, mirroring the kernel (`@vello/identity`): consumer realms log in by phone+OTP
 * (AAL1); operator realms log into a shared console with a credential + mandatory MFA (AAL2). An operator
 * session — MERCHANT **or** BUSINESS — is the substrate the Admin console rides; admin authority itself is the
 * server-side role grants, not the realm.
 */
export const CONSUMER_REALMS = ['RIDER', 'DRIVER'];
export const OPERATOR_REALMS = ['MERCHANT', 'BUSINESS'];
/** Whether `realm` is an operator realm (credential+MFA / AAL2). Accepts an untyped `/me` realm string. */
export function isOperatorRealm(realm) {
    return OPERATOR_REALMS.includes(realm);
}
export class AuthApi {
    client;
    constructor(client) {
        this.client = client;
    }
    requestOtp(input) {
        return this.client.request({ method: 'POST', path: '/v1/auth/otp/request', body: input });
    }
    /**
     * D383: a server-signed human challenge (a proof-of-work) for the phone's last anti-abuse token, bound to this phone +
     * device. Solve it with `solveOtpChallenge` and repeat the send with `humanChallenge`.
     */
    requestOtpChallenge(input) {
        return this.client.request({ method: 'POST', path: '/v1/auth/otp/challenge', body: input });
    }
    /** Re-deliver a NEW code on the current channel (server enforces cooldown + cap; 429 carries retry-after). */
    resendOtp(input) {
        return this.client.request({ method: 'POST', path: '/v1/auth/otp/resend', body: input });
    }
    /** Ask for the SMS fallback (allowed once the primary failed/timed out or after the server's delay). */
    requestSmsFallback(input) {
        return this.client.request({ method: 'POST', path: '/v1/auth/otp/fallback/sms', body: input });
    }
    /** The current delivery view of a flow (after a delivery receipt, or to refresh timers). */
    otpStatus(verificationId) {
        return this.client.request({ method: 'GET', path: `/v1/auth/otp/status?verificationId=${encodeURIComponent(verificationId)}` });
    }
    /** Carries a device-key proof when the client has a proof provider, binding the new session to that key (ADR-0039). */
    verifyOtp(input) {
        return this.client.request({ method: 'POST', path: '/v1/auth/otp/verify', body: input, deviceProof: {} });
    }
    /** Carries a device-key proof bound to this refresh token (`rth`) when the client has a proof provider (ADR-0039). */
    refresh(input) {
        return this.client.request({ method: 'POST', path: '/v1/auth/refresh', body: input, deviceProof: { refreshToken: input.refreshToken } });
    }
    /**
     * End the session with its REFRESH credential (+ a device proof bound to it when the client has a signer) — works after
     * the access token expired, so a console logout / idle sign-out always revokes server-side. 204 on success.
     */
    revokeSession(input) {
        return this.client.request({ method: 'POST', path: '/v1/auth/session/revoke', body: input, expect: 'empty', deviceProof: { refreshToken: input.refreshToken } });
    }
    /** Requires the current access token; the server revokes the session and answers 204. */
    logout() {
        return this.client.request({ method: 'POST', path: '/v1/auth/logout', auth: true, expect: 'empty' });
    }
    me() {
        return this.client.request({ method: 'GET', path: '/v1/me', auth: true });
    }
    /**
     * D445 (R-14): the rider saves first name, last name and/or e-mail (only the given fields change). Stable refusals:
     * `PROFILE_NAME_INVALID`, `PROFILE_EMAIL_INVALID`, `PROFILE_PATCH_EMPTY`, `PROFILE_FIELD_NOT_EDITABLE` (400) and
     * `PROFILE_SELF_EDIT_NOT_OFFERED` (403, a driver session). Answers the account as `me()` does.
     * D805: a console operator saves first name, last name and/or `locale` (audited): `PROFILE_LOCALE_NOT_OFFERED` (400), `OPERATOR_SESSION_REQUIRED` (403,
     * an AAL1 token), `PROFILE_SELF_EDIT_UNAVAILABLE` (503: the audited write failed, nothing changed).
     */
    updateMe(patch) {
        return this.client.request({ method: 'PATCH', path: '/v1/me', body: patch, auth: true });
    }
}
//# sourceMappingURL=auth.js.map